import React, { useContext, useState } from 'react';
import { Box, CheckBoxGroup, FormField, ResponsiveContext, Text, Tip } from "grommet";
import Badge from "../../Badge";
import Avatar from '../../Avatar';
import Modal from '../../Modal';
import StatusMessage from '../../StatusMessage';
import Form from '../../Form';
import { DialogContext } from '../../DialogProvider';
import Icon from '../../Icon';
import GoogleIcon from '../../GoogleIcon';
import MicrosoftIcon from '../../MicrosoftIcon';
import { handleErrors } from '../../../utils/errors';
import {
    IconDots as IconMoreDetails
} from '@tabler/icons-react';
import { formatInitials, formatName } from '../../../utils/names';
import { getPermissionOptions } from '../../../utils/permissions';
import { useEnabledFeatures } from '../../../utils/useEnabledFeatures';
import Menu from '../../Menu';
import { useSWRConfig } from 'swr';

const TeamMemberSection = ({ member={}, showNotification, showPermissionSetting={} }) => {
    const size = useContext(ResponsiveContext);
    const [showPermissionModal, setShowPermissionModal] = useState();
    const { mutate } = useSWRConfig();
    const { hasDepositLinks } = useEnabledFeatures();
    const permissionOptions = getPermissionOptions({ hasDepositLinks });
    const [updateError, setUpdateError] = useState();
    const [formValues, setFormValues] = useState({
        id: member?.id,
        permissions: [...member?.permissions]
    });
    let title = "";
    if (member?.isUser) {
        title = "You";
    }

    const isOwner = member?.owner;
    const isInvited = !member?.is_active && !member?.isUser;

    const updateTeammate = ({ value, callback }) => {
        fetch("/api/settings/team", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(value),
        })
            .then(handleErrors)
            .then((data) => {
                if (callback) callback(data);
                return data;
            })
            .then(() => showNotification({ message: "Permissions updated!" }))
            .catch(setUpdateError);
    };

    const handleSubmit = () => {
        fetch(`/api/settings/team/${member?.id}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        })
            .then(handleErrors)
            .then((data) => {
                if (data) {
                    mutate("/api/settings/team");
                }
            })
            .catch(handleErrors);
    };

    return (
        <Box
            justify="between"
            margin={{ vertical: "small" }}
            direction="row"
            align="center"
        >
            <Box direction="row" align="center" gap="small" flex>
                <Box flex={false}>
                    <Avatar initial={formatInitials(member, true)} />
                </Box>
                <Box direction="column" flex>
                    {/* wrap: on a phone "(Invitation Pending)" drops under the name rather than squeezing it */}
                    <Box direction="row" align="center" wrap>
                        <Box direction="column" skeleton={!member ? {animation: 'fadeIn'} : undefined }>
                            <Text weight="bold">
                                {formatName(member, true)}
                            </Text>
                        </Box>
                        {member?.has_google_oauth && (
                            <Tip content="Signs in with Google">
                                <Box margin={{ horizontal: "xsmall" }}>
                                    <GoogleIcon size={16} />
                                </Box>
                            </Tip>
                        )}
                        {member?.has_microsoft_oauth && (
                            <Tip content="Signs in with Microsoft">
                                <Box margin={{ horizontal: "xsmall" }}>
                                    <MicrosoftIcon size={16} />
                                </Box>
                            </Tip>
                        )}
                        <Box direction="column">
                            {isOwner && (
                                <Badge
                                    margin={{
                                        horizontal: "xsmall",
                                        vertical: "xxsmall",
                                    }}
                                >
                                    Owner
                                </Badge>
                            )}
                            {isInvited && (
                                <Text
                                    weight="bold"
                                    margin={{ horizontal: "xsmall" }}
                                    color="text-xweak"
                                >
                                    (Invitation Pending)
                                </Text>
                            )}
                        </Box>
                        <Box direction="column">
                            {title && (
                                <Text
                                    weight="bold"
                                >{`(${title})`}</Text>
                            )}
                        </Box>
                    </Box>
                    <Box skeleton={!member ? {animation: 'fadeIn'} : undefined }>
                        <Text width={size === "small" ? undefined : {min: "small"}} wordBreak="break-word">{member.email}</Text>
                    </Box>
                </Box>
            </Box>
            {showPermissionModal && (
                <Modal
                    onClose={() => setShowPermissionModal()}
                    title="Update permission"
                >
                    <Box direction="column">
                        {updateError && (
                            <StatusMessage apiError={updateError} />
                        )}
                        <Text margin={{ top: "small" }}>
                            Allow {formatName(member, true)} to access the
                            following tabs:
                        </Text>
                        <Form
                            onReset={() => setShowPermissionModal()}
                            onSubmit={(event) =>
                                updateTeammate({
                                    value: event.value,
                                    callback: () => setShowPermissionModal(),
                                })
                            }
                            onChange={(nextValue) => {
                                setFormValues(nextValue);
                            }}
                            value={formValues}
                            editOnly
                            sectionProps={{
                                margin: "0",
                                border: false,
                                pad: "0",
                                headerProps: {
                                    border: false,
                                },
                                contentProps: {
                                    pad: '0'
                                }
                            }}
                        >
                            <FormField
                                name="permissions"
                                htmlFor="permissions"
                                required
                            >
                                <CheckBoxGroup
                                    id="permissions"
                                    name="permissions"
                                    options={permissionOptions}
                                    labelKey="label"
                                    valueKey="value"
                                />
                            </FormField>
                        </Form>
                    </Box>
                </Modal>
            )}
            {!member?.isUser && !member?.owner && showPermissionSetting && (
                <DialogContext.Consumer>
                    {({ showDialog }) => (
                        <Menu
                            event={{section: 'settings_team_member', element: 'menu', description: member?.id}}
                            responsive={true}
                            size="small"
                            dropProps={{
                                align: {
                                    right: "right",
                                    top: 'bottom'
                                },
                                margin: {
                                    vertical: "xsmall",
                                    horizontal: "xxsmall",
                                },
                            }}
                            icon={
                                <Box
                                    responsive={true}
                                    align="end"
                                    width="20px"
                                    fill="horizontal"
                                >
                                    <Icon
                                        SelectedIcon={IconMoreDetails}
                                        size="small"
                                    />
                                </Box>
                            }
                            items={[
                                {
                                    label: <Text>Update permissions</Text>,
                                    onClick: () => setShowPermissionModal(true),
                                    event: {section: `settings_team_member`, element: 'update_permission'}
                                },
                                {
                                    label: (
                                        <Text color="status-critical">
                                            Remove teammate
                                        </Text>
                                    ),
                                    onClick: () =>
                                        showDialog({
                                            title: "Remove teammate",
                                            message: `Are you sure you would like to remove ${formatName(
                                                member,
                                                true
                                            )} from this team?`,
                                            callback: handleSubmit,
                                            ctaType: 'error'
                                        }),
                                    event: {section: `settings_team_member`, element: 'remove_teammate'}
                                },
                            ]}
                        />
                    )}
                </DialogContext.Consumer>
            )}
        </Box>
    );
};

export default TeamMemberSection;
